/**
 * Pure spaced-repetition helpers using a 5-box Leitner schedule.
 *
 * Boxes:
 *   0 — never reviewed (or reset after a wrong on first encounter)
 *   1 — 1 day until next review
 *   2 — 3 days
 *   3 — 7 days
 *   4 — 14 days
 *   5 — 30 days (near-mastered)
 *
 * Correct → advance one box (cap 5)
 * Wrong   → reset to box 1 (force tomorrow review)
 *
 * No store imports, no React imports — fully testable.
 */

export type ReviewBox = 0 | 1 | 2 | 3 | 4 | 5;

export interface ReviewState {
  box: ReviewBox;
  /** YYYY-MM-DD. When today >= dueAt, the code is "due". */
  dueAt: string;
  /** ISO timestamp of the last review. */
  lastReviewed: string;
  attempts: number;
  correct: number;
}

const INTERVAL_DAYS_BY_BOX: Record<ReviewBox, number> = {
  0: 0,
  1: 1,
  2: 3,
  3: 7,
  4: 14,
  5: 30,
};

export function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

export function addDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().split('T')[0];
}

export function daysBetween(earlier: string, later: string): number {
  const a = new Date(`${earlier}T00:00:00Z`).getTime();
  const b = new Date(`${later}T00:00:00Z`).getTime();
  return Math.floor((b - a) / (1000 * 60 * 60 * 24));
}

/** Compute the next review state given the previous one (or none) and the answer. */
export function nextReviewState(prev: ReviewState | undefined, correct: boolean): ReviewState {
  const now = new Date().toISOString();
  const today = todayStr();

  if (!prev) {
    // First-ever encounter: right → box 1 (review tomorrow); wrong → box 0
    // so the missed code is due *today* and resurfaces in the next session.
    const box: ReviewBox = correct ? 1 : 0;
    return {
      box,
      dueAt: addDays(today, INTERVAL_DAYS_BY_BOX[box]),
      lastReviewed: now,
      attempts: 1,
      correct: correct ? 1 : 0,
    };
  }

  const newBox: ReviewBox = correct
    ? (Math.min(prev.box + 1, 5) as ReviewBox)
    : 0;

  return {
    box: newBox,
    dueAt: addDays(today, INTERVAL_DAYS_BY_BOX[newBox]),
    lastReviewed: now,
    attempts: prev.attempts + 1,
    correct: prev.correct + (correct ? 1 : 0),
  };
}

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

interface SelectOptions {
  /** Fraction of slots reserved for due reviews. Default 0.6 (60/40 due-vs-new). */
  dueRatio?: number;
}

/**
 * Pick `count` codes for a quiz, prioritizing due-for-review codes (most overdue
 * first) then filling with never-seen codes, then with not-due-yet codes.
 *
 * The result is exactly `count` codes if there are enough candidates total.
 * Order is: due-block (random within most-overdue tier), then new (random),
 * then notDue fallback (random). The downstream quiz will shuffle again as
 * needed; we don't want a predictable "all-due then all-new" UX.
 */
export function selectForReview(
  reviews: Record<string, ReviewState> | undefined,
  candidates: string[],
  count: number,
  options: SelectOptions = {},
): string[] {
  const dueRatio = options.dueRatio ?? 0.6;
  const today = todayStr();

  const due: { code: string; daysOverdue: number }[] = [];
  const fresh: string[] = [];
  const notDue: string[] = [];

  for (const code of candidates) {
    const review = reviews?.[code];
    if (!review) {
      fresh.push(code);
    } else if (review.dueAt <= today) {
      due.push({ code, daysOverdue: daysBetween(review.dueAt, today) });
    } else {
      notDue.push(code);
    }
  }

  // Most overdue first
  due.sort((a, b) => b.daysOverdue - a.daysOverdue);

  const targetDue = Math.min(due.length, Math.floor(count * dueRatio));
  const targetFresh = Math.min(fresh.length, count - targetDue);

  const picked: string[] = [];
  picked.push(...due.slice(0, targetDue).map(d => d.code));
  picked.push(...shuffleArray(fresh).slice(0, targetFresh));

  // Fill remaining slots: more due first (if many overdue), then notDue
  if (picked.length < count) {
    const need = count - picked.length;
    picked.push(...due.slice(targetDue, targetDue + need).map(d => d.code));
  }
  if (picked.length < count) {
    const need = count - picked.length;
    picked.push(...shuffleArray(notDue).slice(0, need));
  }

  // Shuffle the final order so the kid doesn't see all due-first
  return shuffleArray(picked);
}

/** Counts how many codes from `candidates` are due today (including overdue). */
export function countDue(
  reviews: Record<string, ReviewState> | undefined,
  candidates: string[],
): number {
  if (!reviews) return 0;
  const today = todayStr();
  let n = 0;
  for (const code of candidates) {
    const r = reviews[code];
    if (r && r.dueAt <= today) n++;
  }
  return n;
}

/** Returns true if a single code is currently due for review. */
export function isDue(review: ReviewState | undefined): boolean {
  if (!review) return false;
  return review.dueAt <= todayStr();
}
